import { COPY } from '@/lib/copy';

type Props = {
  tags: string[];
  tagCounts: Map<string, number>;
  activeTags: Set<string>;
  allCount: number;
  search: string;
  onTagToggle: (tag: string) => void;
  onClearTags: () => void;
  onSearchChange: (value: string) => void;
  filteredCount: number;
};

const { filters } = COPY.blog;

export default function BlogFilters({
  tags,
  tagCounts,
  activeTags,
  allCount,
  search,
  onTagToggle,
  onClearTags,
  onSearchChange,
  filteredCount,
}: Props) {
  return (
    <div
      className="px-[6vw] pt-10 bg-bg-main"
      role="search"
      aria-label={filters.regionLabel}
    >
      <div className="max-w-[1024px] mx-auto pb-6 border-b border-bg-alt flex items-start justify-between gap-8 flex-wrap">
        {/* Tag filters */}
        <div>
          <p
            id="filters-label"
            className="font-sans text-[0.65rem] tracking-[0.2em] uppercase text-[var(--text-light)] mb-3"
          >
            {filters.filterByTag}
          </p>
          <div
            className="flex flex-wrap gap-[0.4rem]"
            role="group"
            aria-labelledby="filters-label"
          >
            <TagButton
              label={filters.allTags}
              count={allCount}
              isActive={activeTags.size === 0}
              onClick={onClearTags}
            />
            {tags.map((tag) => (
              <TagButton
                key={tag}
                label={tag}
                count={tagCounts.get(tag) ?? 0}
                isActive={activeTags.has(tag)}
                onClick={() => onTagToggle(tag)}
              />
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative shrink-0">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-light)] pointer-events-none"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder={filters.searchPlaceholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label={filters.searchLabel}
            className="border border-bg-alt bg-white rounded-full pl-8 pr-8 py-[0.45rem] font-sans text-[0.82rem] text-text outline-none transition-colors focus:border-primary placeholder:text-[var(--text-light)] w-[230px]"
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              aria-label={filters.clearSearch}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 text-[var(--text-light)] hover:text-primary transition-colors cursor-pointer"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
                className="w-3.5 h-3.5"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="max-w-[1024px] mx-auto pt-3 pb-1">
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="font-sans text-[0.7rem] text-[var(--text-light)]"
        >
          {filters.count(filteredCount, allCount)}
        </p>
      </div>
    </div>
  );
}

function TagButton({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 px-[0.85rem] py-[0.38rem] rounded-full border font-sans text-[0.7rem] transition-all whitespace-nowrap cursor-pointer',
        isActive
          ? 'bg-white border-primary text-primary font-semibold'
          : 'bg-white border-bg-alt text-[var(--text-mid)] hover:border-primary hover:text-primary',
      ].join(' ')}
    >
      {label}
      <span
        className={[
          'text-[0.6rem] rounded-full px-[0.4rem] py-[0.1rem] min-w-[18px] text-center transition-all',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'bg-bg-alt text-[var(--text-mid)]',
        ].join(' ')}
      >
        {count}
      </span>
    </button>
  );
}
